class ProductTabsHandler {
  constructor() {
    this.productTabs = document.querySelectorAll('input[name="product-tabs"]');
    this.productTabsWrapper = document.querySelector('.product-tabs-wrapper');

    if (!this.productTabs.length || !this.productTabsWrapper) return;

    // Create or get container for dynamic product section
    this.productContainer = this.getOrCreateProductContainer();

    this.init();
  }

  getOrCreateProductContainer() {
    let container = this.productTabsWrapper.nextElementSibling;

    if (container && container.classList.contains('product-tabs-dynamic-container')) {
      return container;
    }

    // Create new container
    container = document.createElement('div');
    container.className = 'product-tabs-dynamic-container';
    this.productTabsWrapper.parentNode.insertBefore(container, this.productTabsWrapper.nextSibling);

    return container;
  }

  init() {
    // Get current product handle from URL or form
    const currentProductHandle = this.getCurrentProductHandle();

    let foundMatch = false;
    if (currentProductHandle) {
      this.productTabs.forEach((tab) => {
        const tabHandle = tab.getAttribute('data-product-handle')?.trim();
        if (tabHandle === currentProductHandle) {
          tab.checked = true;
          foundMatch = true;
        }
      });
    }

    // If no match found, check the first tab and load it
    if (!foundMatch && this.productTabs.length > 0) {
      const firstTab = this.productTabs[0];
      if (!document.querySelector('input[name="product-tabs"]:checked')) {
        firstTab.checked = true;
        // Load the first product immediately
        this.handleProductChange(firstTab);
        this.handleSlideVisibility(firstTab);
      }
    } else if (foundMatch) {
      // If we found a match, load that product
      const checkedTab = document.querySelector('input[name="product-tabs"]:checked');
      if (checkedTab) {
        this.handleProductChange(checkedTab);
        this.handleSlideVisibility(checkedTab);
      }
    }

    this.productTabs.forEach((tab) => {
      // Prevent click events from bubbling up to avoid triggering other behaviors
      tab.addEventListener('click', (e) => {
        e.stopPropagation();
      });

      tab.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.handleProductChange(e.target);
          this.handleSlideVisibility(e.target);
        }
      });
    });
  }

  getCurrentProductHandle() {
    // Try to get product handle from URL
    const urlPath = window.location.pathname;
    const productMatch = urlPath.match(/\/products\/([^\/\?]+)/);
    if (productMatch) {
      return productMatch[1];
    }

    const productIdInput = document.querySelector('input[name="product-id"]');
    if (productIdInput) {
      const productId = productIdInput.value;
      const matchingTab = Array.from(this.productTabs).find((tab) => tab.value === productId);
      if (matchingTab) {
        return matchingTab.getAttribute('data-product-handle')?.trim();
      }
    }

    return null;
  }

  async handleProductChange(radioButton) {
    const productHandle = radioButton.getAttribute('data-product-handle')?.trim();
    if (!productHandle) return;

    try {
      const productUrl = `/products/${productHandle}?view=scent-kit-landing`;
      const response = await fetch(productUrl);

      if (!response.ok) throw new Error('Failed to fetch product');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the product section in the fetched HTML
      const newProductSection = doc.querySelector('section[id^="MainProduct-"]');

      if (newProductSection) {
        // Update the product section in container
        await this.updateProductSection(newProductSection);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    }
  }

  async updateProductSection(newProductSection) {
    const clonedSection = newProductSection.cloneNode(true);

    // Generate a unique section ID to avoid conflicts
    const uniqueId = `product-tabs-${Date.now()}`;
    const newSectionId = clonedSection.dataset.section || uniqueId;

    // Replace all section IDs in the cloned HTML to use unique ID
    let sectionHTML = clonedSection.outerHTML;
    if (newSectionId) {
      sectionHTML = sectionHTML.replaceAll(new RegExp(newSectionId, 'g'), uniqueId);
    }

    this.productContainer.innerHTML = sectionHTML;

    const insertedSection = this.productContainer.querySelector('section[id^="MainProduct-"]');

    if (insertedSection) {
      this.closeAllAccordions(insertedSection);

      this.reinitializeScripts(insertedSection);

      this.updateProductElements(insertedSection);

      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('shopify:section:load'));
      });
    }
  }

  closeAllAccordions(productSection) {
    const allDetails = productSection.querySelectorAll('details');
    allDetails.forEach((details) => {
      details.removeAttribute('open');
      const summary = details.querySelector('summary');
      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
      }
    });
  }

  reinitializeScripts(productSection) {
    if (!productSection) return;

    // Run scripts in the updated section
    const scripts = productSection.querySelectorAll('script');
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    if (window.Shopify && window.Shopify.PaymentButton) {
      window.Shopify.PaymentButton.init();
    }

    if (window.ProductModel) {
      window.ProductModel.loadShopifyXR();
    }

    productSection.querySelectorAll('details summary').forEach((summary) => {
      summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

      if (summary.nextElementSibling && summary.nextElementSibling.getAttribute('id')) {
        summary.setAttribute('aria-controls', summary.nextElementSibling.id);
      }
    });

    // Reinitialize delivery countdown for the new product section
    this.reinitializeDeliveryCountdown(productSection);

    document.dispatchEvent(new CustomEvent('product:updated'));
  }

  reinitializeDeliveryCountdown(productSection) {
    // Shared with the PDP (assets/delivery-countdown.js) so variant dispatch overrides apply here too
    if (typeof DeliveryCountdown !== 'undefined') new DeliveryCountdown(productSection);
  }

  updateProductElements(productSection) {
    this.initializeTabbedLinks(productSection);
  }

  initializeTabbedLinks(productSection) {
    const tabbedLinks = productSection.querySelectorAll('.main-product__tabbed-link');

    tabbedLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();

        const targetId = link.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          if (targetElement.classList.contains('product__accordion')) {
            const detailsElement = targetElement.querySelector('details');

            if (detailsElement && !detailsElement.hasAttribute('open')) {
              detailsElement.setAttribute('open', '');

              const summaryElement = detailsElement.querySelector('summary');
              if (summaryElement) {
                summaryElement.setAttribute('aria-expanded', 'true');
              }
            }
          }

          // Scroll to the element
          const offset = 100;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });

          targetElement.focus({ preventScroll: true });
        }
      });
    });
  }

  handleSlideVisibility(radioButton) {
    const blockId = radioButton.getAttribute('data-block-id');
    if (!blockId) return;

    // Find all slide wrappers with matching block-id
    const allSlides = document.querySelectorAll('.section-image-with-usps__slide-wrapper');

    // Hide all slides first
    allSlides.forEach((slide) => {
      slide.classList.remove('active');
    });

    // Show matching slides and apply background color
    allSlides.forEach((slide) => {
      const slideBlockId = slide.getAttribute('data-block-id');
      if (slideBlockId === blockId) {
        slide.classList.add('active');

        // Apply background color from the slide's data-color attribute
        const slideColor = slide.getAttribute('data-color');
        if (slideColor) {
          slide.style.backgroundColor = slideColor;
        }
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductTabsHandler();
  });
} else {
  new ProductTabsHandler();
}
